<template>
  <!-- Table Container Card -->
  <b-card>

    <validation-observer ref="trialBalanceRules">
      <b-form @submit.prevent="handleSubmit">
        <b-row class="match-height">
          <!-- Branch -->
          <b-col md="3">
            <SelectField
              id="branch"
              v-model="periodicTrialBalanceFormData.branch"
              label="Branch"
              :options="branchOptions"
              :reduce="option => option"
              item="label"
              placeholder="Select"
            />
          </b-col>
          <!-- Branch -->

          <!-- Level -->
          <b-col md="3">
            <SelectField
              id="level"
              v-model="periodicTrialBalanceFormData.level"
              label="Level"
              :options="levelOptions"
              :reduce="option => option.value"
              error-name="Level"
              rules="required"
              item="label"
              placeholder="Select"
            />
          </b-col>
          <!-- Level -->

          <!-- Start Date-->
          <b-col md="2">
            <DatePickerField
              id="start-date"
              v-model="periodicTrialBalanceFormData.startDate"
              rules="required"
              :min-date="new Date()"
              label="Start Date"
              error-name="Start Date"
              :config="datePickerConfig"
              placeholder="Select Date"
            />
          </b-col>
          <!--  Start Date -->

          <!-- End Date-->
          <b-col md="2">
            <DatePickerField
              id="end-date"
              v-model="periodicTrialBalanceFormData.endDate"
              rules="required"
              :min-date="new Date()"
              label="End Date"
              error-name="End Date"
              :config="datePickerConfig"
              placeholder="Select Date"
            />
          </b-col>
          <!--  End Date -->

          <b-col
            md="2"
            class="mt-2"
          >
            <Button
              label="Generate Report"
              :loading="isLoading"
              :disabled="isDisabled"
              variant="primary"
              size="md"
              @click="handleSubmit"
            />
          </b-col>
        </b-row>
      </b-form>
    </validation-observer>

    <div class="m-2">

      <!-- Table Top -->
      <b-row>

        <!-- Per Page -->
        <b-col
          cols="12"
          md="6"
          class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
        />
        <!-- Export -->
        <b-col
          cols="12"
          md="6"
        >
          <div class="d-flex align-items-center justify-content-end">
            <b-dropdown
              v-if="reportData.length && getUserPermission('EXPORT_TRIAL_BALANCE_REPORT')"
              id="dropdown-1"
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              text="Export"
              variant="primary"
            >
              <b-dropdown-item @click="csvExport(csvData)">
                CSV
              </b-dropdown-item>
              <b-dropdown-item @click="excelExport(csvData)">
                Excel
              </b-dropdown-item>
              <b-dropdown-item @click="pdfExport">
                PDF
              </b-dropdown-item>
              <b-dropdown-item @click="printData">
                Print
              </b-dropdown-item>
            </b-dropdown>
          </div>
        </b-col>

      </b-row>

    </div>
    <div id="periodic-trial-balance">
      <b-card
        id="prints"
        style="text-align: center;"
      >balanceSheetFormData.financialDate
        <h2>{{ userDetails.institutionName }}</h2>
        <h4> Periodic Trial Balance Report</h4>
        <h5>Date Range: {{ formatCustomDate(periodicTrialBalanceFormData.startDate) }} - {{ formatCustomDate(periodicTrialBalanceFormData.endDate) }}</h5>
        <h5>{{ periodicTrialBalanceFormData.branch.label }} - {{ periodicTrialBalanceFormData.branch.value }}</h5>
        <h5>{{ `Level ${periodicTrialBalanceFormData.level}` }}</h5>
      </b-card>
      <b-table
        v-if="reportData.length"
        ref="glTableRef"
        :items="reportData"
        :busy="isBusy"
        :per-page="3000"
        :current-page="currentPage"
        responsive
        :fields="tableHeaders"
        primary-key="id"
        show-empty
        empty-text="No records available"
        class="position-relative"
      >
        <template #table-busy>
          <div class="text-center text-danger my-2">
            <b-spinner
              class="align-middle"
              variant="primary"
            />
          </div>
        </template>

      </b-table>
    </div>

    <FullScreenSpinner :show="showSpinner" />
  </b-card>

</template>

<script>
import {
  BCard, BRow, BForm, BCol, BTable, BSpinner, BDropdown, BDropdownItem,
} from 'bootstrap-vue'
import { tableMixins } from '@core/mixins/table'
import { modalMixins } from '@core/mixins/modal'
import { sweetAlert } from '@core/mixins/sweetAlert'
import { permissionMixin } from '@core/mixins/permission'
import { mapActions, mapGetters } from 'vuex'
import Ripple from 'vue-ripple-directive'
import { ValidationObserver } from 'vee-validate'
import { required } from '@validations'
import jsPDF from 'jspdf'
import 'jspdf-autotable'
import XLSX from 'xlsx'
import { reportDataDetail } from '../../../../helpers/data'
import SelectField from '../../../forms/SelectField.vue'
import DatePickerField from '../../../forms/DatePickerField.vue'
import Button from '../../../components/button/Button.vue'
import FullScreenSpinner from '@/views/components/spinner/FullScreenSpinner.vue'
import { toYearWithDate, toCustomDate } from '@/helpers/formatDate'

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BSpinner,
    BTable,
    BDropdown,
    BForm,
    Button,
    BDropdownItem,
    SelectField,
    DatePickerField,
    ValidationObserver,
    FullScreenSpinner,
  },
  directives: {
    Ripple,
  },
  mixins: [modalMixins, tableMixins, sweetAlert, permissionMixin],
  data() {
    return {
      isBusy: false,
      tableHeaders: [
        { key: 'description' },
        { key: 'level1Code', label: 'Level 1 Reporting' },
        { key: 'level2Code', label: 'Level 2 Reporting' },
        { key: 'level3Code', label: 'Level 3 Reporting' },
        { key: 'level4Code', label: 'Level 4 Reporting' },
        { key: 'debit' },
        { key: 'credit' },
        { key: 'closingBalance' },
      ],
      levelOptions: [
        { label: 'Level 1', value: 1 },
        { label: 'Level 2', value: 2 },
        { label: 'Level 3', value: 3 },
        { label: 'Level 4', value: 4 },
      ],
      required,
      isLoading: false,
      isDisabled: false,
      showSpinner: false,
      reportData: [],
      reportInfo: reportDataDetail,
      periodicTrialBalanceFormData: {
        branch: '',
        level: '',
        startDate: '',
        endDate: '',
      },
      datePickerConfig: {
        dateFormat: 'd/m/Y',
        maxDate: 'today',
        defaultDate: '',
      },
    }
  },
  computed: {
    csvData() {
      return this.reportData.map(item => ({
        Description: item.description,
        'Level 1 Reporting': item.level1Code,
        'Level 2 Reporting': item.level2Code,
        'Level 3 Reporting': item.level3Code,
        'Level 4 Reporting': item.level4Code,
        Debit: item.debit,
        Credit: item.credit,
        'Closing Balance': item.closingBalance,
      }))
    },
    ...mapGetters({
      branchOptions: 'operations/branchOptions',
      userDetails: 'auth/getCurrentUser',
    }),
  },
  methods: {
    ...mapActions({
      getPeriodicTrialBalance: 'reports/getPeriodicTrialBalance',
    }),
    async fetchTrialBalance() {
      try {
        this.showSpinner = true
        const response = await this.getPeriodicTrialBalance({
          ...this.periodicTrialBalanceFormData,
          branchCode: this.periodicTrialBalanceFormData.branch.value,
          startDate: toYearWithDate(this.periodicTrialBalanceFormData.startDate),
          endDate: toYearWithDate(this.periodicTrialBalanceFormData.endDate),
        })
        const { data, status } = response
        if (status === 200 && data.data) {
          this.reportData = data.data
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.showSpinner = false
      }
    },

    csvExport(arrData) {
      const institutionName = this.userDetails.institutionName.toUpperCase()
      let csvContent = 'data:text/csv;charset=utf-8,'
      csvContent += [
        [institutionName],
        ['Periodic Trial Balance'],
        [`${(this.periodicTrialBalanceFormData.startDate !== null && this.periodicTrialBalanceFormData.endDate !== null) ? `Date Range: ${this.formatCustomDate(this.periodicTrialBalanceFormData.startDate)} - ${this.formatCustomDate(this.periodicTrialBalanceFormData.endDate)}` : 'Date Range: All'}`],
        [`${(this.periodicTrialBalanceFormData.branch !== '') ? `${this.periodicTrialBalanceFormData.branch.label}` : 'Branch: All'}`],
        [`Level ${this.periodicTrialBalanceFormData.level}`],
        [],

        Object.keys(arrData[0]).join(','),
        ...arrData.map(item => Object.values(item).join(',')),
      ]
        .join('\n')
        .replace(/(^\[)|(\]$)/gm, '')

      const dataValue = encodeURI(csvContent)
      const link = document.createElement('a')
      link.setAttribute('href', dataValue)
      link.setAttribute('download', 'Periodic Trial Balance.csv')
      link.click()
    },

    excelExport(arrData) {
      const institutionName = this.userDetails.institutionName.toUpperCase()
      const filename = 'Periodic Trial Balance.xlsx'
      const headerLists = [
        Object.keys(arrData[0]),
      ]

      const Heading = []
      const Data = arrData // All data lists
      const ws = XLSX.utils.aoa_to_sheet(Heading)
      XLSX.utils.sheet_add_aoa(ws, [
        [institutionName],
        ['Periodic Trial Balance'],
        [`${(this.periodicTrialBalanceFormData.startDate !== null && this.periodicTrialBalanceFormData.endDate !== null) ? `Date Range: ${this.formatCustomDate(this.periodicTrialBalanceFormData.startDate)} - ${this.formatCustomDate(this.periodicTrialBalanceFormData.endDate)}` : 'Date Range: All'}`],
        [`${(this.periodicTrialBalanceFormData.branch !== '') ? `${this.periodicTrialBalanceFormData.branch.label}` : 'Branch: All'}`],
        [`Level ${this.periodicTrialBalanceFormData.level}`],
      ], { origin: 'D1' })

      XLSX.utils.sheet_add_aoa(ws, headerLists, { origin: 'A6' })

      XLSX.utils.sheet_add_json(ws, Data, {
        skipHeader: true,
        origin: 'A8',
      })
      const wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, ws, 'Periodic Trial Balance')
      XLSX.writeFile(wb, filename)
    },

    pdfExport() {
      const pdfData = this.reportData.map(item => ({
        ...item,
      }))

      const columns = [
        { title: 'Description', key: 'description' },
        { title: 'Level 1', key: 'level1Code' },
        { title: 'Level 2', key: 'level2Code' },
        { title: 'Level 3', key: 'level3Code' },
        { title: 'Level 4', key: 'level4Code' },
        { title: 'Debit', key: 'debit' },
        { title: 'Credit', key: 'credit' },
        { title: 'Closing Balance', key: 'closingBalance' },
      ]

      const doc = jsPDF('portrait', 'pt', 'a4')
      const institutionName = this.userDetails.institutionName.toUpperCase()
      doc.setFontSize(20)
      doc.text(180, 60, `${institutionName}`)
      doc.setFontSize(15)
      doc.text(180, 80, 'Periodic Trial Balance')
      doc.setFontSize(12)

      doc.text(180, 95, `${(this.periodicTrialBalanceFormData.startDate !== null && this.periodicTrialBalanceFormData.endDate !== null) ? `Date Range: ${this.formatCustomDate(this.periodicTrialBalanceFormData.startDate)} - ${this.formatCustomDate(this.periodicTrialBalanceFormData.endDate)}` : 'Date Range: All'}`)
      doc.text(180, 110, `${(this.periodicTrialBalanceFormData.branch !== '') ? `${this.periodicTrialBalanceFormData.branch.label}` : 'Branch: All'}`)
      doc.text(180, 125, `Level ${this.periodicTrialBalanceFormData.level}`)

      doc.autoTable({
        theme: 'striped', // grid, plain
        columns,
        body: pdfData,
        startY: 150,
        // split overflowing columns into pages
        horizontalPageBreak: true,
        // repeat this column in split pages
        horizontalPageBreakRepeat: 'id',
        tableWidth: 'auto', // 'auto', 'wrap' or a number,
        margin: {
          horizontal: 20,
          vertical: 20,
        },
        bodyStyles: {
          valign: 'top',
        },
        styles: {
          overflow: 'linebreak',
          cellWidth: 'wrap',
          maxCellHeight: 4,
          fontSize: 8,
          cellPadding: 5,
        },
        columnStyles: {
          text: {
            cellWidth: 'wrap',
          },
        },
      })
      // Footer page number
      const pages = doc.internal.getNumberOfPages()
      const pageWidth = doc.internal.pageSize.width
      const pageHeight = doc.internal.pageSize.height
      doc.setFontSize(5)

      for (let j = 1; j < pages + 1; j += 1) {
        const horizontalPos = pageWidth / 2 // Can be fixed number
        const verticalPos = pageHeight - 10 // Can be fixed number
        doc.setPage(j)
        doc.text(`page ${j} of ${pages}`, horizontalPos, verticalPos, { align: 'center' }) // Optional text styling});
      }
      doc.save('Periodic Trial Balance.pdf')
    },
    printData() {
      const divToPrint = document.getElementById('periodic-trial-balance')
      const newWin = window.open('')
      newWin.document.write(divToPrint.outerHTML)
      newWin.print()
      newWin.close()
    },

    handleSubmit() {
      this.$refs.trialBalanceRules.validate().then(async success => {
        if (success) {
          this.fetchTrialBalance()
        }
      })
    },
    formatCustomDate: toCustomDate,
  },
}
</script>

<style lang="scss">
    @import '@core/scss/vue/libs/vue-select.scss';
      @media screen {
  #prints {
    display: none;
   }
}

@media print {
 body * {
  visibility:hidden;
  }
  #print, #print * {
    visibility:visible;
  }
  #print {
    position:absolute;
    left:0;
    top:0;
  }
}
</style>
